/**
 * The persisted equipment profile document.
 *
 * Mirrors core's `EquipmentProfile` field-for-field — same names, same units,
 * same meanings — and adds only what persistence needs: an id, notes, a
 * `builtin` flag and timestamps. Optional core fields become `T | null` per the
 * document-model rule in `./common.ts`.
 *
 * Three built-ins ship here so the recipe editor works before M4's preset
 * library lands (PLAN §11). They are ordinary documents with `builtin: true`:
 * the reducer refuses to delete or edit them, and a user who wants a variant
 * duplicates one into their own profile.
 */

import { z } from 'zod';
import {
  documentIdSchema,
  nameSchema,
  nonNegativeSchema,
  notesSchema,
  percentSchema,
  positiveSchema,
  spargeTypeSchema,
  timestampSchema,
} from './common';

/** The fields of a persisted equipment profile, before {@link migrateEquipmentProfileDoc}. */
/**
 * The decoction fields, **without** their document defaults.
 *
 * They exist separately because `equipment.update`'s patch is built by
 * `.partial()`-ing the document schema, and `.partial()` does not strip a
 * `.default()`: the key materialises on every patch that omits it, so a command
 * changing only the boil-off rate would silently reset the brewer's standing
 * loss and pot size too. The document needs the defaults (a profile saved
 * before decoction existed must still parse); the patch must not have them.
 *
 * This is the rule `recipeFermentationStepChangesSchema` already states for
 * `endTempC`, which restates the field without its default for exactly this
 * reason. It is written as a shared constant here rather than restated inline
 * because there are three fields rather than one — but it is the same rule, and
 * any future defaulted field on any of these documents needs it.
 */
export const DECOCTION_FIELDS = {
  mashStandingLossCPerHour: nonNegativeSchema.max(50),
  decoctionBoilOffLPerHour: nonNegativeSchema.max(50),
  decoctionPotVolumeL: positiveSchema.max(10_000).nullable(),
} as const;

/**
 * The fermenter's capacity, without its document default.
 *
 * The same rule {@link DECOCTION_FIELDS} states, for the one field added after
 * it: a profile saved before the pre-brew checks existed must still parse, so the
 * document needs `.default(null)`, and `equipmentChangesSchema` must not have it
 * or every patch that omits the key would silently blank the brewer's number.
 */
export const FERMENTER_VOLUME_FIELD = {
  fermenterVolumeL: positiveSchema.max(10_000).nullable(),
} as const;

/**
 * A chore the brewer's kit asks for on brew day: swapping the condensate bucket
 * on a steam condenser every half hour, say, or dropping the immersion chiller
 * in with 15 minutes left to sanitise it.
 *
 * It lives on the equipment profile rather than on a recipe because the chore
 * belongs to the hardware, and it reaches brew day the way everything else on
 * the profile does, through the batch's frozen equipment copy. There, each
 * occurrence becomes an ordinary boil step pinned to the boil's clock, so it
 * counts down, alerts and ticks off exactly like a hop addition.
 *
 * A reminder either repeats (`everyMinutes`) or happens once
 * (`atMinutesLeft`). The two are separate strict shapes, so a reminder carrying
 * both keys or neither is refused, and every reminder saved before the once
 * shape existed is a repeating one and still parses.
 */
const brewReminderBase = {
  /**
   * Stable id, unique within the profile, minted by the editor as `newId('br')`
   * (39 characters). Capped at 40 because it is spliced into a step id,
   * `boil-rem-<id>-<minute>`, and that has to stay inside the 64 characters
   * `DOCUMENT_ID_PATTERN` allows.
   */
  id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,39}$/),
  /** What to do, in the brewer's words, e.g. `Swap condensate bucket`. */
  label: z.string().trim().min(1).max(60),
  /**
   * When it runs. Only the boil today, because that is the only window with a
   * clock every step can pin to. The field exists so a whirlpool or heat-up
   * window can be added later without reshaping saved profiles.
   */
  window: z.literal('boil'),
};

export const brewReminderSchema = z.union([
  z.strictObject({
    ...brewReminderBase,
    /**
     * Minutes between occurrences. Whole minutes, 5 to 240: anything tighter is
     * a job to stand beside, not one to be reminded of, and four hours is longer
     * than any boil.
     */
    everyMinutes: z.number().int().min(5).max(240),
  }),
  z.strictObject({
    ...brewReminderBase,
    /**
     * Minutes of boil remaining when it comes due, counted the way hop
     * additions are. Whole minutes, 1 to 240. The floor is lower than a
     * repeat's because a single reminder at 2 minutes left is reasonable;
     * flame-out itself has its own step.
     */
    atMinutesLeft: z.number().int().min(1).max(240),
  }),
]);

/** @see brewReminderSchema */
export type BrewReminder = z.infer<typeof brewReminderSchema>;

/**
 * The brew-day reminders, without their document default.
 *
 * The same rule {@link DECOCTION_FIELDS} states: `.partial()` does not strip a
 * `.default()`, so if `equipmentChangesSchema` kept `.default([])` every patch
 * that omits the key would carry an empty list, and a command changing only the
 * boil-off rate would silently delete the brewer's reminders. The document needs
 * the default (a profile saved before reminders existed must still parse); the
 * patch must not have it.
 *
 * An update carries the whole list and replaces it, so undo restores the
 * previous list whole. Ids must be unique within the list, because each one
 * names a family of brew-day steps.
 */
export const BREW_REMINDERS_FIELD = {
  brewReminders: z
    .array(brewReminderSchema)
    .max(8)
    .superRefine((reminders, ctx) => {
      const seen = new Set<string>();
      for (const [index, reminder] of reminders.entries()) {
        if (seen.has(reminder.id)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Two reminders share an id.',
            path: [index, 'id'],
          });
        }
        seen.add(reminder.id);
      }
    }),
} as const;

export const equipmentProfileDocFieldsSchema = z.strictObject({
  /** Stable profile id, unique within the workspace. */
  id: documentIdSchema,
  /** Display name, e.g. `Anvil Foundry 10.5 gal`. */
  name: nameSchema,
  /** True for the profiles this package ships; those are read-only. */
  builtin: z.boolean(),
  /** Brewer's prose about the system. */
  notes: notesSchema,
  /** Lautering strategy. */
  spargeType: spargeTypeSchema,
  /**
   * Volume below the mash tun outlet / false bottom that returns to the kettle
   * (all-in-one systems), **litres**. Does not change water requirements.
   */
  mashTunDeadspaceL: nonNegativeSchema.max(100),
  /** Wort genuinely left behind in the mash tun, **litres**. Adds to the water needed. */
  mashTunLossL: nonNegativeSchema.max(100),
  /** Water retained by spent grain, **litres per kilogram**. */
  grainAbsorptionLPerKg: nonNegativeSchema.max(5),
  /** Default mash thickness, **litres per kilogram of grain**. */
  mashThicknessLPerKg: positiveSchema.max(20),
  /** Evaporation at a rolling boil, **litres per hour**. */
  boilOffRateLPerHour: nonNegativeSchema.max(100),
  /** Thermal contraction from boiling to 20 °C, **%** of the hot volume. */
  boilShrinkagePercent: percentSchema,
  /** Wort left in the kettle below the pickup, **litres**. */
  kettleDeadspaceL: nonNegativeSchema.max(100),
  /** Wort retained by kettle hops, **mL/g** (≡ L/kg). */
  hopAbsorptionMlPerG: nonNegativeSchema.max(20),
  /** Wort retained by dry hops in the fermenter, **mL/g**. */
  dryHopAbsorptionMlPerG: nonNegativeSchema.max(20),
  /** Wort left in the chiller and hoses, **litres**. */
  chillerLossL: nonNegativeSchema.max(100),
  /** Beer left on the trub/yeast cake, **litres**. */
  fermenterTrubLossL: nonNegativeSchema.max(100),
  /** Water added to the kettle after lautering, **litres**. */
  topUpKettleL: nonNegativeSchema.max(1000),
  /** Water added to the fermenter after transfer, **litres**. */
  topUpFermenterL: nonNegativeSchema.max(1000),
  /** Mash efficiency, **%**. */
  mashEfficiencyPercent: percentSchema,
  /** Brewhouse efficiency, **%** — recorded from batch actuals, informational on input. */
  brewhouseEfficiencyPercent: percentSchema,
  /** Mash tun thermal mass as an equivalent **litres of water**. */
  mashTunThermalMassLEquivalent: nonNegativeSchema.max(100),
  /** Mash tun temperature at dough-in, **°C**. */
  mashTunInitialTempC: z.number().finite().min(-20).max(110),
  /** Kettle capacity, **litres**, or `null`. Only used to raise a boil-over warning. */
  kettleVolumeL: positiveSchema.max(10_000).nullable(),
  /** Mash tun capacity, **litres**, or `null`. Only used to raise an overflow warning. */
  mashTunVolumeL: positiveSchema.max(10_000).nullable(),
  /**
   * Fermenter capacity, **litres**, or `null`. Only used to raise a headspace
   * check. Defaulted, so a profile saved before the checks existed still parses.
   */
  fermenterVolumeL: FERMENTER_VOLUME_FIELD.fermenterVolumeL.default(null),
  /**
   * How fast the resting mash loses heat, **°C per hour**. Only decoction reads
   * it; a rig that holds mash temperature actively should be 0.
   *
   * `.default(2)` so a profile saved before decoction existed still parses —
   * the rule `WORKSPACE_SCHEMA_VERSION` states, that a defaulted key does not
   * bump the stamp. Two follow it for the same reason.
   */
  mashStandingLossCPerHour: DECOCTION_FIELDS.mashStandingLossCPerHour.default(2),
  /** Evaporation from the decoction pot, **litres per hour**. */
  decoctionBoilOffLPerHour: DECOCTION_FIELDS.decoctionBoilOffLPerHour.default(1.5),
  /** Decoction pot capacity, **litres**, or `null`. Only used to raise an overflow warning. */
  decoctionPotVolumeL: DECOCTION_FIELDS.decoctionPotVolumeL.default(null),
  /**
   * Chores the kit asks for through the boil, each expanded into boil steps on
   * brew day. Defaulted, so a profile saved before reminders existed still
   * parses, and so does every batch's frozen copy of one.
   */
  brewReminders: BREW_REMINDERS_FIELD.brewReminders.default([]),
  /** Creation time, **ms since epoch**. */
  createdAt: timestampSchema,
  /** Last-modification time, **ms since epoch**. */
  updatedAt: timestampSchema,
});

/**
 * Read a profile saved before `mashTunLossL` existed.
 *
 * The first build had one mash-tun field, `mashTunDeadspaceL`, and treated it
 * as unrecoverable wort: the volume waterfall added it to the water the brewer
 * had to measure out. Brewfather splits the same idea in two — a *deadspace*
 * below the outlet that returns to the kettle and costs nothing, and a *loss*
 * that really is gone — and brewers entering their Brewfather deadspace here
 * were watching their strike water inflate by a vendor's under-basket volume.
 *
 * So the field changed meaning, and the stored number has to move with the
 * meaning it was written under. The rule is conservative: **whatever a saved
 * profile called deadspace was a loss**, because that is what this app did with
 * it. No brewer's computed volumes change because of the migration itself; only
 * the catalog presets were reclassified, and those are templates, copied at
 * creation time, so no existing profile is touched by that either.
 *
 * A document that already carries `mashTunLossL` is left alone, which is what
 * makes this idempotent and safe to run on every read.
 */
export function migrateEquipmentProfileDoc(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const rec = raw as Record<string, unknown>;
  if (rec.mashTunLossL !== undefined) return raw;
  const legacy = typeof rec.mashTunDeadspaceL === 'number' ? rec.mashTunDeadspaceL : 0;
  return { ...rec, mashTunDeadspaceL: 0, mashTunLossL: legacy };
}

/** A persisted equipment profile. Old documents are lifted on the way in. */
export const equipmentProfileDocSchema = z.preprocess(
  migrateEquipmentProfileDoc,
  equipmentProfileDocFieldsSchema,
);

/** @see equipmentProfileDocSchema */
export type EquipmentProfileDoc = z.infer<typeof equipmentProfileDocFieldsSchema>;

/** The id of the profile a fresh workspace defaults to. */
export const DEFAULT_EQUIPMENT_PROFILE_ID = 'builtin-allgrain-20l';

/**
 * The shipped profiles, in the order the picker should show them.
 *
 * Numbers are the conventional homebrew figures, chosen round so the volume
 * waterfall stays arithmetically checkable:
 *
 * | | 5 gal cooler | 5 gal BIAB | 2.5 gal all-grain |
 * | --- | --- | --- | --- |
 * | sparge | batch | full-volume | batch |
 * | grain absorption | 1.0 L/kg | 0.8 L/kg (squeezed bag) | 1.0 L/kg |
 * | mash thickness | 3.0 L/kg | 6.0 L/kg (whole liquor in the kettle) | 3.0 L/kg |
 * | boil-off | 3.0 L/h (~0.8 gal/h) | 3.0 L/h | 1.5 L/h (narrower pot) |
 * | mash efficiency | 75 % | 72 % (no sparge rinse) | 75 % |
 *
 * `builtin: true` and both timestamps are 0 — these documents predate the
 * workspace, and stamping them with a wall clock would break replay
 * determinism.
 */
export const BUILTIN_EQUIPMENT_PROFILES: readonly EquipmentProfileDoc[] = [
  {
    id: DEFAULT_EQUIPMENT_PROFILE_ID,
    name: 'Generic 5 gal all-grain (cooler + kettle)',
    builtin: true,
    notes:
      'A batch-sparged cooler mash tun into a separate kettle — the classic American homebrew all-grain rig, sized for a 5 gallon batch. Round numbers throughout; recalibrate boil-off and mash efficiency from your first few batches.',
    spargeType: 'batch',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 3.0,
    boilOffRateLPerHour: 3.0,
    boilShrinkagePercent: 4,
    kettleDeadspaceL: 1.0,
    hopAbsorptionMlPerG: 6,
    dryHopAbsorptionMlPerG: 6,
    chillerLossL: 0,
    fermenterTrubLossL: 1.0,
    topUpKettleL: 0,
    topUpFermenterL: 0,
    mashEfficiencyPercent: 75,
    brewhouseEfficiencyPercent: 70,
    mashTunThermalMassLEquivalent: 0,
    mashTunInitialTempC: 20,
    kettleVolumeL: 35,
    mashTunVolumeL: 30,
    fermenterVolumeL: 30,
    mashStandingLossCPerHour: 2,
    decoctionBoilOffLPerHour: 1.5,
    decoctionPotVolumeL: null,
    brewReminders: [],
    createdAt: 0,
    updatedAt: 0,
  },
  {
    id: 'builtin-biab-20l',
    name: 'Generic 5 gal BIAB (full volume)',
    builtin: true,
    notes:
      'One vessel, whole liquor in from the start, bag lifted and squeezed. Sized for a 5 gallon batch. No sparge, so mash efficiency runs a few points below a batch-sparged rig, and a squeezed bag gives back roughly 0.8 L/kg instead of 1.0.',
    spargeType: 'biab-full-volume',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 6.0,
    boilOffRateLPerHour: 3.0,
    boilShrinkagePercent: 4,
    kettleDeadspaceL: 1.0,
    hopAbsorptionMlPerG: 6,
    dryHopAbsorptionMlPerG: 6,
    chillerLossL: 0,
    fermenterTrubLossL: 1.0,
    topUpKettleL: 0,
    topUpFermenterL: 0,
    mashEfficiencyPercent: 72,
    brewhouseEfficiencyPercent: 67,
    mashTunThermalMassLEquivalent: 0,
    mashTunInitialTempC: 20,
    kettleVolumeL: 40,
    mashTunVolumeL: 40,
    fermenterVolumeL: 30,
    mashStandingLossCPerHour: 0,
    decoctionBoilOffLPerHour: 1.5,
    decoctionPotVolumeL: null,
    brewReminders: [],
    createdAt: 0,
    updatedAt: 0,
  },
  {
    id: 'builtin-allgrain-10l',
    name: 'Generic 2.5 gal all-grain',
    builtin: true,
    notes:
      'A half-size batch-sparged rig for 2.5 gallon test brews. Fixed losses are halved and the boil-off rate is lower because a smaller pot has less surface area — not because the batch is smaller.',
    spargeType: 'batch',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 3.0,
    boilOffRateLPerHour: 1.5,
    boilShrinkagePercent: 4,
    kettleDeadspaceL: 0.5,
    hopAbsorptionMlPerG: 6,
    dryHopAbsorptionMlPerG: 6,
    chillerLossL: 0,
    fermenterTrubLossL: 0.5,
    topUpKettleL: 0,
    topUpFermenterL: 0,
    mashEfficiencyPercent: 75,
    brewhouseEfficiencyPercent: 70,
    mashTunThermalMassLEquivalent: 0,
    mashTunInitialTempC: 20,
    kettleVolumeL: 20,
    mashTunVolumeL: 18,
    fermenterVolumeL: 15,
    mashStandingLossCPerHour: 2,
    decoctionBoilOffLPerHour: 1.5,
    decoctionPotVolumeL: null,
    brewReminders: [],
    createdAt: 0,
    updatedAt: 0,
  },
];
